import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { formatIST, itemsLabel, itemNames } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import api from '@/lib/api';
import { toast } from 'sonner';
import {
  Truck, Package, CheckCircle2, Eye, UserCircle, Search, RefreshCw, Plus, ChevronDown, ChevronRight,
} from 'lucide-react';
import { getProductTypeName } from '@/lib/constants';
import { validateField, formatApiError } from '@/lib/validators';

import { Screen, ScreenSplit, Pane, CommandBar, StatStrip, DataGrid, CellButton, SidePane } from '@/components/data';

/**
 * Loading a truck, on one screen.
 *
 * Creating a dispatch used to open a dialog that re-listed the very orders you
 * were already looking at, then asked for a vehicle and a driver whose lists
 * lived in two other tabs. Three surfaces to answer one question: what is going
 * out, on what, with whom.
 *
 * Now the ready orders are ticked in the real list and the truck being loaded
 * builds up in the rail beside them. Active and completed dispatches are one
 * grid with a status filter rather than two tabs; vehicles and drivers stay
 * addable from the rail, where they are needed, instead of owning a tab each.
 *
 * Every line on the load carries the pieces going, filled in with everything
 * that is ready. Type a smaller number and only that many go: 10,000 boxes are
 * packed, 5,000 fit in the van, and the other 5,000 are back on this list
 * tomorrow morning.
 */

const money = (v) => (v ? `₹${Number(v).toLocaleString('en-IN')}` : '—');
const pcs = (n) => `${Number(n || 0).toLocaleString('en-IN')} pcs`;

/** Which runs are standing there — a total alone cannot be checked against the floor. */
const batchDetail = (item) => (item.ready_batches || [])
  .map(b => `Batch #${b.batch_number} — ${pcs(b.quantity)}`)
  .join('\n');

export default function DispatchPage() {
  const navigate = useNavigate();
  const [stats, setStats] = useState({});
  const [readyOrders, setReadyOrders] = useState([]);
  const [dispatches, setDispatches] = useState([]);
  const [historyDispatches, setHistoryDispatches] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);

  const [view, setView] = useState('ready');
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState(() => new Set());

  // the load being built
  const [picked, setPicked] = useState([]);
  // only the lines somebody typed a smaller number on; everything else sends
  // all of what is ready, which is the ordinary evening
  const [partQty, setPartQty] = useState({});
  const [vehicleMode, setVehicleMode] = useState('list');
  const [driverMode, setDriverMode] = useState('list');
  const [form, setForm] = useState({
    vehicle_id: '', vehicle_number: '', vehicle_type: '',
    driver_id: '', driver_name: '', driver_mobile: '',
  });
  const [submitting, setSubmitting] = useState(false);

  const [addPane, setAddPane] = useState(null); // 'vehicle' | 'driver'
  const [newVehicle, setNewVehicle] = useState({ vehicle_number: '', vehicle_type: 'Truck', capacity: '' });
  const [newDriver, setNewDriver] = useState({ name: '', mobile_number: '' });

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [statsRes, readyRes, activeRes, histRes, vehRes, drvRes] = await Promise.all([
        api.get('/dispatch/stats'),
        api.get('/dispatch/ready-orders'),
        api.get('/dispatch/dispatches?status=active'),
        api.get('/dispatch/dispatches?status=completed'),
        api.get('/vehicles?status=active&available=true'),
        api.get('/drivers?status=active&available=true'),
      ]);
      setStats(statsRes.data);
      setReadyOrders(readyRes.data);
      setDispatches(activeRes.data.dispatches);
      setHistoryDispatches(histRes.data.dispatches);
      setVehicles(vehRes.data);
      setDrivers(drvRes.data);
    } catch {
      toast.error('Failed to load dispatch data');
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const togglePick = (orderId) => {
    setPicked(prev => prev.includes(orderId) ? prev.filter(id => id !== orderId) : [...prev, orderId]);
    // an order taken off the load forgets the smaller numbers typed on it
    setPartQty(prev => {
      if (!prev[orderId]) return prev;
      const next = { ...prev };
      delete next[orderId];
      return next;
    });
  };

  const clearLoad = () => { setPicked([]); setPartQty({}); };

  /** How many pieces of this line are going — everything ready unless typed over. */
  const goingQty = (order, item) => {
    const typed = partQty[order.id]?.[item.id];
    if (typed === undefined) return item.ready_quantity ?? item.quantity ?? 0;
    return typed === '' ? 0 : Number(typed);
  };

  const setGoingQty = (order, item, value) => {
    const ready = item.ready_quantity ?? item.quantity ?? 0;
    const cleaned = value === '' ? '' : String(Math.max(0, Math.min(ready, Math.floor(Number(value) || 0))));
    setPartQty(prev => ({ ...prev, [order.id]: { ...prev[order.id], [item.id]: cleaned } }));
  };

  const sendAll = (order) => setPartQty(prev => {
    const next = { ...prev };
    delete next[order.id];
    return next;
  });

  const orderGoing = (order) => (order.ready_items || [])
    .reduce((sum, item) => sum + goingQty(order, item), 0);

  const createDispatch = async () => {
    const vehicle = vehicles.find(v => v.id === form.vehicle_id);
    const driver = drivers.find(d => d.id === form.driver_id);
    const vNum = vehicleMode === 'list' ? vehicle?.vehicle_number || '' : form.vehicle_number;
    const vType = vehicleMode === 'list' ? vehicle?.vehicle_type || '' : form.vehicle_type;
    const dName = driverMode === 'list' ? driver?.name || '' : form.driver_name;
    const dMobile = driverMode === 'list' ? driver?.mobile_number || '' : form.driver_mobile;

    const vehicleErr = validateField('vehicleNumber', vNum);
    if (vehicleErr) { toast.error(vehicleErr); return; }
    const nameErr = validateField('required', dName, [{ name: 'required', options: { message: 'Driver name is required' } }]);
    if (nameErr) { toast.error(nameErr); return; }
    const mobileErr = validateField('mobile', dMobile, [{ name: 'mobile', options: { allowEmpty: false } }]);
    if (mobileErr) { toast.error(mobileErr); return; }
    if (picked.length === 0) { toast.error('Tick at least one order to load'); return; }

    // only an order that has pieces to count can be set to nothing by mistake;
    // one booked without a count has no number to type and goes as it is
    const empty = pickedOrders.find(o => (o.ready_quantity || 0) > 0 && orderGoing(o) <= 0);
    if (empty) {
      toast.error(
        `${itemsLabel(empty.ready_items, empty.customer_name)} is set to 0 — `
        + 'type how many pieces are going, or take it off the load',
      );
      return;
    }

    setSubmitting(true);
    try {
      await api.post('/dispatch/dispatches', {
        vehicle_id: vehicleMode === 'list' ? form.vehicle_id : null,
        vehicle_number: vNum,
        vehicle_type: vType,
        is_external_vehicle: vehicleMode === 'external',
        driver_id: driverMode === 'list' ? form.driver_id : null,
        driver_name: dName,
        driver_mobile: dMobile,
        is_external_driver: driverMode === 'external',
        order_ids: picked,
        // what is going, line by line — the server sends exactly this and
        // leaves the rest standing
        loads: pickedOrders.map(o => ({
          order_id: o.id,
          items: (o.ready_items || [])
            .map(item => ({
              item_id: item.id,
              quantity: goingQty(o, item),
              ready: item.ready_quantity ?? item.quantity ?? 0,
            }))
            // a line set to 0 stays behind; a line that has no count at all is
            // 0 because nobody typed one, and it goes
            .filter(line => line.quantity > 0 || line.ready === 0)
            .map(({ item_id, quantity }) => ({ item_id, quantity })),
        })),
      });
      toast.success(`Dispatch booked — ${pcs(pickedQuantity)} from ${picked.length} order${picked.length === 1 ? '' : 's'}`);
      clearLoad();
      setForm({ vehicle_id: '', vehicle_number: '', vehicle_type: '', driver_id: '', driver_name: '', driver_mobile: '' });
      fetchAll();
    } catch (e) {
      toast.error(formatApiError(e, 'Failed to create dispatch'));
    }
    setSubmitting(false);
  };

  const markDelivered = async (dispatchId, orderIds) => {
    try {
      // one at a time — the server rewrites the whole trip on every call
      for (const orderId of orderIds) {
        await api.put(`/dispatch/dispatches/${dispatchId}/orders/${orderId}/deliver`);
      }
      toast.success(orderIds.length > 1
        ? `${orderIds.length} orders marked as delivered`
        : 'Order marked as delivered');
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
      fetchAll();
    }
  };

  const addVehicle = async () => {
    if (!newVehicle.vehicle_number?.trim()) { toast.error('Vehicle number is required'); return; }
    try {
      await api.post('/vehicles', newVehicle);
      toast.success('Vehicle added');
      setAddPane(null);
      setNewVehicle({ vehicle_number: '', vehicle_type: 'Truck', capacity: '' });
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const addDriver = async () => {
    if (!newDriver.name?.trim()) { toast.error('Driver name is required'); return; }
    const mobileErr = validateField('mobile', newDriver.mobile_number, [{ name: 'mobile', options: { allowEmpty: false } }]);
    if (mobileErr) { toast.error(mobileErr); return; }
    try {
      await api.post('/drivers', newDriver);
      toast.success('Driver added');
      setAddPane(null);
      setNewDriver({ name: '', mobile_number: '' });
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const matches = (text) => !search || text?.toLowerCase().includes(search.toLowerCase());
  const orderMatches = (o) => !search || matches(o.order_id) || matches(o.voice_number)
    || matches(o.customer_name) || matches(o.firm_name);

  const filteredReady = useMemo(() => readyOrders.filter(orderMatches),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [readyOrders, search]);

  // Active and completed in one list — they are the same record at two ages.
  const allDispatches = useMemo(() => {
    const rows = [];
    const source = view === 'history' ? historyDispatches
      : view === 'active' ? dispatches
        : [...dispatches, ...historyDispatches];
    for (const d of source) {
      if (search && !d.orders?.some(orderMatches)) continue;
      rows.push({ ...d, __rowId: d.id, __kind: 'dispatch' });
      if (expanded.has(d.id)) {
        for (const o of d.orders || []) {
          // trips booked before the per-order stamp existed fall back to the trip's own time
          rows.push({ ...o, __rowId: `${d.id}:${o.id}`, __kind: 'order', __depth: 1,
            __dispatchId: d.id, __dispatchDate: d.dispatch_date });
        }
      }
    }
    return rows;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatches, historyDispatches, view, expanded, search]);

  const toggleExpanded = useCallback((id) => setExpanded(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  }), []);

  const showValue = readyOrders.some(o => o.order_value != null);

  const readyColumns = useMemo(() => ([
    {
      key: 'pick',
      header: '',
      width: '2.25rem',
      render: (order) => (
        <span onClick={(e) => e.stopPropagation()}>
          <Checkbox
            checked={picked.includes(order.id)}
            onCheckedChange={() => togglePick(order.id)}
            aria-label={`Load ${order.customer_name}`}
            className="h-3.5 w-3.5"
          />
        </span>
      ),
    },
    {
      key: 'item',
      header: 'Item / Customer',
      width: 'minmax(13rem,1.5fr)',
      render: (order) => (
        <>
          <span className="block truncate font-semibold text-slate-900"
            title={itemNames(order.ready_items)}>
            {itemsLabel(order.ready_items, order.customer_name)}
          </span>
          <span className="block truncate text-[11px] leading-tight text-slate-500">
            {order.customer_name}{order.firm_name ? ` — ${order.firm_name}` : ''}
          </span>
          <span className="block truncate text-[11px] leading-tight text-slate-400">{order.order_id}</span>
        </>
      ),
    },
    {
      key: 'items',
      mobile: false,
      header: 'Standing ready',
      width: 'minmax(12rem,1.4fr)',
      render: (order) => (
        <>
          <span className="flex flex-wrap gap-1">
            {(order.ready_items || []).slice(0, 3).map(item => {
              // a split job loads a batch at a time, so the number that matters
              // is what is standing at Dispatch — not what the whole item comes to
              const ready = item.ready_quantity ?? item.quantity;
              const partial = item.ready_quantity != null && item.ready_quantity < item.quantity;
              return (
                <span key={item.id} className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] text-slate-600"
                  title={batchDetail(item)}>
                  {(order.ready_items.length > 1)
                    && `${item.item_name || getProductTypeName(item.order_type)} · `}
                  <span className="font-medium tabular-nums text-slate-700">{pcs(ready)}</span>
                  {partial && <span className="text-amber-600"> of {pcs(item.quantity)}</span>}
                  {item.ready_batch_count > 0 && item.batch_count > 1 && (
                    <span className="text-slate-400">
                      {' '}· {item.ready_batch_count} of {item.batch_count} batches
                    </span>
                  )}
                </span>
              );
            })}
            {(order.ready_items?.length || 0) > 3 && (
              <span className="text-[10px] text-slate-400">+{order.ready_items.length - 3}</span>
            )}
          </span>
          {/* an order is rarely all-or-nothing — say how much of it is waiting,
              in pcs when it is one item part-made, in items when it is several */}
          {!order.fully_ready && (
            <span className="block text-[10px] leading-tight text-amber-600">
              {order.total_item_count > 1
                ? `${order.ready_item_count} of ${order.total_item_count} items`
                : `${pcs(order.ready_quantity)} of ${pcs(order.total_quantity)}`}
              {' '}standing — the rest is still in production
            </span>
          )}
          {/* pieces that went on an earlier van, so nobody counts them twice */}
          {order.sent_quantity > 0 && (
            <span className="block text-[10px] leading-tight text-slate-500">
              {pcs(order.sent_quantity)} already sent earlier
            </span>
          )}
        </>
      ),
      // a loading list totals in pcs; with the value gone this is the number
      // that says how big the load is
      total: (rows) => pcs(rows.reduce((s, o) => s + (o.ready_quantity || 0), 0)),
    },
    {
      key: 'priority',
      header: 'Priority',
      width: '5.5rem',
      render: (order) => (
        <span className={`text-[11px] font-medium ${
          order.priority === 'high' ? 'text-red-600' : order.priority === 'low' ? 'text-emerald-600' : 'text-amber-600'
        }`}>{order.priority}</span>
      ),
    },
    {
      key: 'due',
      header: 'Due',
      width: '6.5rem',
      render: (order) => (
        <span className="whitespace-nowrap text-[11px] text-slate-600">
          {order.expected_delivery_date ? formatIST(order.expected_delivery_date, { dateOnly: true }) : '—'}
        </span>
      ),
    },
    // What the goods are worth is the office's business: the server sends it
    // only to the roles allowed to see it, and the column follows what arrived.
    ...(showValue ? [{
      key: 'value',
      mobile: false,
      header: 'Value',
      align: 'right',
      width: '7rem',
      render: (order) => <span className="font-medium">{money(order.order_value)}</span>,
      total: (rows) => money(rows.reduce((s, o) => s + (o.order_value || 0), 0)),
    }] : []),
    {
      key: 'actions',
      header: '',
      width: '3rem',
      align: 'right',
      render: (order) => (
        <CellButton icon={Eye} label="Open order" iconOnly onClick={() => navigate(`/orders/${order.id}`)} />
      ),
    },
  ]), [picked, navigate, showValue]);

  const dispatchColumns = useMemo(() => ([
    {
      key: 'name',
      header: 'Dispatch / Order',
      width: 'minmax(13rem,1.4fr)',
      render: (row) => row.__kind === 'order' ? (
        <span className="min-w-0">
          <span className="block truncate font-medium text-slate-800">
            {itemsLabel((row.item_names || []).map(name => ({ item_name: name })), row.customer_name)}
          </span>
          <span className="block truncate text-[11px] text-slate-500">
            {row.customer_name}{row.firm_name ? ` — ${row.firm_name}` : ''}
          </span>
          <span className="block truncate text-[10px] text-slate-400">
            {row.order_id}
            {row.dispatched_quantity ? ` · ${pcs(row.dispatched_quantity)} sent` : ''}
          </span>
          {/* what went short — the question somebody rings up about */}
          {row.is_part_load && (
            <span className="block truncate text-[10px] text-amber-600">
              Part load · {pcs(row.pending_quantity)} still to send
            </span>
          )}
        </span>
      ) : (
        <span className="flex min-w-0 items-center gap-1.5">
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); toggleExpanded(row.id); }}
            aria-label={expanded.has(row.id) ? 'Collapse orders' : 'Expand orders'}
            className="shrink-0 rounded p-0.5 text-slate-400 hover:bg-slate-200 hover:text-slate-700"
          >
            {expanded.has(row.id) ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
          </button>
          <span className="truncate font-semibold text-slate-900">{row.dispatch_number}</span>
          <span className="shrink-0 text-[10px] text-slate-400">{row.orders?.length || 0} orders</span>
        </span>
      ),
    },
    {
      key: 'vehicle',
      mobile: false,
      header: 'Vehicle',
      width: '10rem',
      render: (row) => row.__kind === 'order' ? null : (
        <span className="flex min-w-0 items-center gap-1 text-[12px] text-slate-600">
          <Truck className="h-3 w-3 shrink-0 text-slate-400" />
          <span className="truncate">{row.vehicle_number}{row.is_external_vehicle ? ' (Ext)' : ''}</span>
        </span>
      ),
    },
    {
      key: 'driver',
      header: 'Driver',
      width: '10rem',
      render: (row) => row.__kind === 'order' ? null : (
        <span className="flex min-w-0 items-center gap-1 text-[12px] text-slate-600">
          <UserCircle className="h-3 w-3 shrink-0 text-slate-400" />
          <span className="truncate">{row.driver_name}{row.is_external_driver ? ' (Ext)' : ''}</span>
        </span>
      ),
    },
    {
      // when the van left, not just the day it left — a dispatcher chasing a
      // load needs the hour, and the completed list is the record of it
      key: 'date',
      mobile: false,
      header: 'Dispatched',
      width: '9.5rem',
      render: (row) => {
        const at = row.__kind === 'order' ? (row.dispatched_at || row.__dispatchDate) : row.dispatch_date;
        return (
          <span className="whitespace-nowrap text-[11px] text-slate-500">
            {at ? formatIST(at, { compact: true }) : '—'}
            {row.__kind === 'order' && row.delivered_at && (
              <span className="block text-[10px] leading-tight text-emerald-600">
                delivered {formatIST(row.delivered_at, { compact: true })}
              </span>
            )}
          </span>
        );
      },
    },
    {
      key: 'status',
      header: 'Status',
      width: '7rem',
      render: (row) => {
        if (row.__kind === 'order') {
          return (
            <span className={`text-[11px] font-medium ${
              row.delivery_status === 'delivered' ? 'text-emerald-600' : 'text-blue-600'
            }`}>
              {row.delivery_status === 'delivered' ? 'Delivered' : 'In Transit'}
            </span>
          );
        }
        const done = (row.orders || []).every(o => o.delivery_status === 'delivered');
        return (
          <span className={`text-[11px] font-medium ${done ? 'text-emerald-600' : 'text-blue-600'}`}>
            {done ? 'Completed' : 'On the road'}
          </span>
        );
      },
    },
    {
      key: 'actions',
      header: '',
      width: '8.5rem',
      align: 'right',
      render: (row) => {
        // the trip is marked from its own row — expanding it to click every
        // order on the van is the same answer typed several times
        if (row.__kind !== 'order') {
          const pending = (row.orders || []).filter(o => o.delivery_status !== 'delivered');
          if (pending.length === 0) return null;
          return (
            <CellButton
              icon={CheckCircle2}
              label="Mark delivered"
              tone="good"
              onClick={() => markDelivered(row.id, pending.map(o => o.id))}
              testId={`deliver-trip-${row.id}`}
            />
          );
        }
        return (
          <span className="flex items-center justify-end gap-0.5">
            {row.delivery_status !== 'delivered' && (
              <CellButton
                icon={CheckCircle2}
                label="Delivered"
                tone="good"
                onClick={() => markDelivered(row.__dispatchId, [row.id])}
              />
            )}
            <CellButton icon={Eye} label="Open order" iconOnly onClick={() => navigate(`/orders/${row.id}`)} />
          </span>
        );
      },
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ]), [expanded, toggleExpanded, navigate]);

  const pickedOrders = readyOrders.filter(o => picked.includes(o.id));
  // a load is counted in pieces, not orders — half an order can be on the van,
  // and pieces is the number that decides whether it all fits
  const pickedQuantity = pickedOrders.reduce((n, o) => n + orderGoing(o), 0);
  const pickedReady = pickedOrders.reduce((n, o) => n + (o.ready_quantity || 0), 0);

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-brand-blue" />
      </div>
    );
  }

  return (
    <Screen data-testid="dispatch-page">
      <CommandBar
        title="Dispatch"
        subtitle={picked.length ? `${picked.length} order${picked.length === 1 ? '' : 's'} on the load` : undefined}
        actions={[
          { key: 'ready', label: 'Ready', icon: Package, tone: view === 'ready' ? 'primary' : 'default', onClick: () => setView('ready') },
          { key: 'active', label: 'On the road', icon: Truck, tone: view === 'active' ? 'primary' : 'default', onClick: () => setView('active') },
          { key: 'history', label: 'History', icon: CheckCircle2, tone: view === 'history' ? 'primary' : 'default', onClick: () => setView('history') },
          { key: 'refresh', label: 'Refresh', icon: RefreshCw, onClick: fetchAll },
        ]}
      >
        <div className="relative w-full max-w-xs">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
          <Input
            className="h-7 pl-8 text-xs"
            placeholder="Order ID, voice number, customer…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </CommandBar>

      <StatStrip
        stats={[
          { key: 'ready', label: 'Ready for dispatch', value: stats.ready_for_dispatch || 0, tone: 'warn' },
          { key: 'active', label: 'Active dispatches', value: stats.active_dispatches || 0, tone: 'blue' },
          { key: null, label: 'In transit', value: stats.dispatched_orders || 0 },
          { key: 'history', label: 'Completed', value: stats.completed_dispatches || 0, tone: 'good' },
        ]}
        activeKey={view}
        onSelect={(key) => setView(key || 'ready')}
      />

      <ScreenSplit
        railWidth={view === 'ready' ? '21rem' : '0rem'}
        rail={view === 'ready' ? (
          <Pane
            title="Load being built"
            aside={picked.length
              ? `${picked.length} order${picked.length === 1 ? '' : 's'} · ${pcs(pickedQuantity)}${
                pickedQuantity < pickedReady ? ' · part load' : ''}`
              : 'tick orders to start'}
            className="max-h-full"
          >
            <div className="space-y-2.5 p-2.5">
              <div>
                <div className="mb-1 flex items-center justify-between">
                  <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Vehicle</span>
                  <span className="flex gap-1">
                    {['list', 'external'].map(mode => (
                      <button
                        key={mode}
                        type="button"
                        onClick={() => setVehicleMode(mode)}
                        className={`rounded px-1.5 py-0.5 text-[10px] capitalize ${
                          vehicleMode === mode ? 'bg-brand-blue text-white' : 'text-slate-500 hover:bg-slate-100'
                        }`}
                      >
                        {mode === 'list' ? 'Ours' : 'Hired'}
                      </button>
                    ))}
                  </span>
                </div>
                {vehicleMode === 'list' ? (
                  <div className="flex gap-1">
                    <Select value={form.vehicle_id} onValueChange={(v) => setForm(f => ({ ...f, vehicle_id: v }))}>
                      <SelectTrigger className="h-7 text-xs"><SelectValue placeholder="Select vehicle" /></SelectTrigger>
                      <SelectContent>
                        {vehicles.map(v => (
                          <SelectItem key={v.id} value={v.id} className="text-xs">
                            {v.vehicle_number} — {v.vehicle_type}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button variant="outline" size="sm" className="h-7 shrink-0 px-2"
                      onClick={() => setAddPane('vehicle')} title="Add a vehicle">
                      <Plus className="h-3 w-3" />
                    </Button>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 gap-1 sm:grid-cols-2">
                    <Input className="h-7 text-xs" placeholder="Number *"
                      value={form.vehicle_number} onChange={e => setForm(f => ({ ...f, vehicle_number: e.target.value }))} />
                    <Input className="h-7 text-xs" placeholder="Type"
                      value={form.vehicle_type} onChange={e => setForm(f => ({ ...f, vehicle_type: e.target.value }))} />
                  </div>
                )}
              </div>

              <div>
                <div className="mb-1 flex items-center justify-between">
                  <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Driver</span>
                  <span className="flex gap-1">
                    {['list', 'external'].map(mode => (
                      <button
                        key={mode}
                        type="button"
                        onClick={() => setDriverMode(mode)}
                        className={`rounded px-1.5 py-0.5 text-[10px] ${
                          driverMode === mode ? 'bg-brand-blue text-white' : 'text-slate-500 hover:bg-slate-100'
                        }`}
                      >
                        {mode === 'list' ? 'Ours' : 'Hired'}
                      </button>
                    ))}
                  </span>
                </div>
                {driverMode === 'list' ? (
                  <div className="flex gap-1">
                    <Select value={form.driver_id} onValueChange={(v) => setForm(f => ({ ...f, driver_id: v }))}>
                      <SelectTrigger className="h-7 text-xs"><SelectValue placeholder="Select driver" /></SelectTrigger>
                      <SelectContent>
                        {drivers.map(d => (
                          <SelectItem key={d.id} value={d.id} className="text-xs">
                            {d.name} — {d.mobile_number}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button variant="outline" size="sm" className="h-7 shrink-0 px-2"
                      onClick={() => setAddPane('driver')} title="Add a driver">
                      <Plus className="h-3 w-3" />
                    </Button>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 gap-1 sm:grid-cols-2">
                    <Input className="h-7 text-xs" placeholder="Name *"
                      value={form.driver_name} onChange={e => setForm(f => ({ ...f, driver_name: e.target.value }))} />
                    <Input className="h-7 text-xs" placeholder="Mobile *"
                      value={form.driver_mobile} onChange={e => setForm(f => ({ ...f, driver_mobile: e.target.value }))} />
                  </div>
                )}
              </div>

              <div className="rounded border border-slate-200" data-testid="load-lines">
                <p className="border-b border-slate-200 bg-slate-50 px-2 py-1 text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                  On the load
                </p>
                {pickedOrders.length === 0 ? (
                  <p className="px-2 py-3 text-center text-[11px] text-slate-400">
                    Tick orders in the list to load them.
                  </p>
                ) : pickedOrders.map(o => {
                  const going = orderGoing(o);
                  const staying = (o.ready_quantity || 0) - going;
                  return (
                    <div key={o.id} className="border-b border-slate-100 px-2 py-1.5 last:border-b-0">
                      <div className="flex items-start justify-between gap-2">
                        <span className="min-w-0">
                          <span className="block truncate text-[12px] text-slate-700">{o.customer_name}</span>
                          <span className="block truncate text-[10px] text-slate-400">{o.order_id}</span>
                        </span>
                        <button
                          type="button"
                          onClick={() => togglePick(o.id)}
                          aria-label={`Remove ${o.order_id} from the load`}
                          className="shrink-0 rounded px-1 text-[10px] text-slate-400 hover:bg-red-50 hover:text-red-600"
                        >
                          remove
                        </button>
                      </div>

                      {(o.ready_items || []).map(item => {
                        const ready = item.ready_quantity ?? item.quantity ?? 0;
                        const typed = partQty[o.id]?.[item.id];
                        return (
                          <div key={item.id} className="mt-1 rounded bg-slate-50 px-1.5 py-1">
                            <span className="block truncate text-[11px] font-medium text-slate-700">
                              {item.item_name || getProductTypeName(item.order_type)}
                            </span>
                            {/* nothing to count on this one — it was booked
                                without a quantity, so there is no number to ask
                                for and the whole of it goes */}
                            {ready === 0 ? (
                              <span className="mt-0.5 block text-[10px] text-slate-500">
                                No piece count on this item — it goes as it is.
                              </span>
                            ) : (
                            <span className="mt-0.5 flex items-center gap-1.5">
                              <Input
                                type="number"
                                min="0"
                                max={ready}
                                className="h-6 w-[4.5rem] px-1.5 text-right text-[11px] tabular-nums"
                                value={typed === undefined ? String(ready) : typed}
                                onChange={(e) => setGoingQty(o, item, e.target.value)}
                                aria-label={`Pieces of ${item.item_name || 'this item'} going out`}
                                data-testid={`load-qty-${item.id}`}
                              />
                              <span className="text-[10px] text-slate-500">of {pcs(ready)} ready</span>
                              {typed !== undefined && (
                                <button
                                  type="button"
                                  onClick={() => sendAll(o)}
                                  className="ml-auto shrink-0 rounded px-1 text-[10px] text-brand-blue hover:bg-blue-50"
                                >
                                  send all
                                </button>
                              )}
                            </span>
                            )}
                          </div>
                        );
                      })}

                      {staying > 0 && (
                        <p className="mt-1 text-[10px] leading-tight text-amber-600">
                          Sending {pcs(going)} — {pcs(staying)} stay here for the next trip.
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>

              <Button
                className="w-full bg-brand-blue text-white hover:bg-brand-blue-dark"
                disabled={submitting || picked.length === 0}
                onClick={createDispatch}
                data-testid="confirm-create-dispatch"
              >
                <Truck className="mr-1.5 h-4 w-4" />
                {submitting ? 'Creating…' : picked.length ? `Send ${pcs(pickedQuantity)}` : 'Dispatch'}
              </Button>
            </div>
          </Pane>
        ) : null}
      >
        {view === 'ready' ? (
          <Pane
            title="Ready for dispatch"
            aside={`${filteredReady.length} order${filteredReady.length === 1 ? '' : 's'}`}
            scroll={false}
            bodyClassName="flex flex-col"
            actions={filteredReady.length > 0 && (
              <button
                type="button"
                onClick={() => (picked.length ? clearLoad() : setPicked(filteredReady.map(o => o.id)))}
                className="rounded px-1.5 py-0.5 text-[10px] font-normal normal-case tracking-normal text-blue-100 hover:bg-white/20"
              >
                {picked.length ? 'Clear' : 'Select all'}
              </button>
            )}
          >
            <DataGrid
              data-testid="ready-orders-table"
              columns={readyColumns}
              rows={filteredReady}
              getRowId={(o) => o.id}
              onRowClick={(o) => togglePick(o.id)}
              showTotals={filteredReady.length > 0}
              empty={
                <span className="flex flex-col items-center gap-2 text-slate-400">
                  <Package className="h-8 w-8 text-slate-300" />
                  No orders ready for dispatch
                </span>
              }
            />
          </Pane>
        ) : (
          <Pane
            title={view === 'history' ? 'Completed dispatches' : 'On the road'}
            aside={`${allDispatches.filter(r => r.__kind === 'dispatch').length}`}
            scroll={false}
            bodyClassName="flex flex-col"
          >
            <DataGrid
              data-testid="dispatches-table"
              columns={dispatchColumns}
              rows={allDispatches}
              getRowId={(row) => row.__rowId}
              empty={
                <span className="flex flex-col items-center gap-2 text-slate-400">
                  <Truck className="h-8 w-8 text-slate-300" />
                  {view === 'history' ? 'No dispatch history yet' : 'Nothing on the road'}
                </span>
              }
            />
          </Pane>
        )}
      </ScreenSplit>

      <SidePane
        open={addPane === 'vehicle'}
        onClose={() => setAddPane(null)}
        title="Add vehicle"
        subtitle="Available to every dispatch from now on"
        width="24rem"
        footer={
          <>
            <Button variant="outline" size="sm" onClick={() => setAddPane(null)}>Cancel</Button>
            <Button size="sm" className="bg-brand-blue text-white hover:bg-brand-blue-dark" onClick={addVehicle}>
              Add vehicle
            </Button>
          </>
        }
      >
        <div className="space-y-2 p-3">
          <Input placeholder="Vehicle number *" value={newVehicle.vehicle_number}
            onChange={e => setNewVehicle(p => ({ ...p, vehicle_number: e.target.value }))} />
          <Input placeholder="Type (Truck, Van…)" value={newVehicle.vehicle_type}
            onChange={e => setNewVehicle(p => ({ ...p, vehicle_type: e.target.value }))} />
          <Input placeholder="Capacity (e.g. 2 Ton)" value={newVehicle.capacity}
            onChange={e => setNewVehicle(p => ({ ...p, capacity: e.target.value }))} />
          <p className="pt-1 text-[11px] text-slate-500">
            {vehicles.length} vehicle{vehicles.length === 1 ? '' : 's'} on file.
          </p>
        </div>
      </SidePane>

      <SidePane
        open={addPane === 'driver'}
        onClose={() => setAddPane(null)}
        title="Add driver"
        subtitle="Available to every dispatch from now on"
        width="24rem"
        footer={
          <>
            <Button variant="outline" size="sm" onClick={() => setAddPane(null)}>Cancel</Button>
            <Button size="sm" className="bg-brand-blue text-white hover:bg-brand-blue-dark" onClick={addDriver}>
              Add driver
            </Button>
          </>
        }
      >
        <div className="space-y-2 p-3">
          <Input placeholder="Driver name *" value={newDriver.name}
            onChange={e => setNewDriver(p => ({ ...p, name: e.target.value }))} />
          <Input placeholder="Mobile number *" value={newDriver.mobile_number}
            onChange={e => setNewDriver(p => ({ ...p, mobile_number: e.target.value }))} />
          <p className="pt-1 text-[11px] text-slate-500">
            {drivers.length} driver{drivers.length === 1 ? '' : 's'} on file.
          </p>
        </div>
      </SidePane>
    </Screen>
  );
}
